/**
 * TeacherResources — bucket 4 of 4 ("My resources"). Renders the design's
 * three-module layout: Training & certifications, Library, What's new.
 *
 * Content-authoring (scripts, care guides, trainings, announcements) has no
 * source yet — it's a separate track tracked in BON-66 — so the whole surface
 * renders the design layout as an em-dash skeleton behind a single
 * <MockedOverlay>, mirroring RegionalCorp (BON-86). It lights up when the
 * content model ships.
 */
import { MOBILE_MAX, useMediaQuery } from '../../../lib/useMediaQuery';
import { MockedOverlay } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';
import MobileTeacherResources from '../responsive/MobileTeacherResources';

import { useTeacherResources } from './teacherApi';

const EM_DASH = '—';
const TRAINING_ROWS = ['t1', 't2', 't3', 't4', 't5'] as const;
const LIBRARY_TABS = [
  'All',
  'Scripts',
  'Care guides',
  'Troubleshoot',
  'Policies',
  'Brand',
] as const;
const LIBRARY_CARDS = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'] as const;
const NEWS_ROWS = ['n1', 'n2', 'n3'] as const;

const heading: React.CSSProperties = { fontSize: 13, letterSpacing: '0.04em', margin: 0 };
const subtle = 'var(--nb-subtle)';
const border = '1px solid var(--nb-border)';

function TrainingCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          Training &amp; certifications
        </h3>
        <span className="nb-eyebrow">
          {EM_DASH}/{EM_DASH} complete
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {TRAINING_ROWS.map((r) => (
          <div
            key={r}
            style={{
              padding: '8px 10px',
              background: 'var(--nb-surface)',
              borderRadius: 7,
              border,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: subtle }}>{EM_DASH}</span>
              <span
                className="nb-eyebrow"
                style={{
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: 'var(--nb-surface-2)',
                  color: subtle,
                }}
              >
                {EM_DASH}
              </span>
            </div>
            <div style={{ marginTop: 6 }}>
              <div className="nb-bar" style={{ width: '100%', height: 5 }}>
                <i style={{ width: '0%' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LibraryCard() {
  return (
    <section
      className="nb-card"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          Library
        </h3>
        <div
          style={{
            display: 'flex',
            background: 'var(--nb-surface)',
            border,
            borderRadius: 999,
            padding: 2,
          }}
        >
          {LIBRARY_TABS.map((tab) => (
            <span
              key={tab}
              style={{
                height: 22,
                padding: '0 10px',
                fontSize: 10.5,
                fontWeight: 600,
                lineHeight: '22px',
                borderRadius: 999,
                color: subtle,
              }}
            >
              {tab}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {LIBRARY_CARDS.map((c) => (
          <div
            key={c}
            style={{ padding: '11px 12px', background: 'var(--nb-bg)', border, borderRadius: 8 }}
          >
            <div className="nb-eyebrow" style={{ color: subtle }}>
              {EM_DASH}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: subtle, marginTop: 6 }}>
              {EM_DASH}
            </div>
            <div style={{ fontSize: 11, color: subtle, marginTop: 4, lineHeight: 1.4 }}>
              {EM_DASH}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 10,
                color: subtle,
                marginTop: 8,
                paddingTop: 6,
                borderTop: '1px dashed var(--nb-border)',
              }}
            >
              <span>Updated {EM_DASH}</span>
              <span>{EM_DASH}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhatsNewCard() {
  return (
    <section className="nb-card" style={{ flex: 1, minHeight: 0 }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          What&apos;s new
        </h3>
        <span className="nb-eyebrow">{EM_DASH} this month</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {NEWS_ROWS.map((r) => (
          <div
            key={r}
            style={{
              padding: '8px 10px',
              background: 'var(--nb-surface)',
              borderRadius: 7,
              border,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: subtle }}>{EM_DASH}</span>
              <span style={{ fontSize: 10, color: subtle }}>{EM_DASH}</span>
            </div>
            <div style={{ fontSize: 11, color: subtle, lineHeight: 1.4 }}>{EM_DASH}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function TeacherResources() {
  const isMobile = useMediaQuery(MOBILE_MAX);
  if (isMobile) return <MobileTeacherResources />;
  return <TeacherResourcesDesktop />;
}

function TeacherResourcesDesktop() {
  const q = useTeacherResources();

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  return (
    <MockedOverlay reason="Teaching resources aren’t wired up yet. Goes live once the content-authoring story ships — tracked in BON-66.">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.65fr) minmax(0, 1fr)',
          gap: 14,
        }}
      >
        <LibraryCard />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <TrainingCard />
          <WhatsNewCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
